import styled from '@emotion/styled';
import { useListingContext } from '~/editor/plugin/ListingPlugin';
import type { HeroBlockData } from '~/editor/blocks/hero/HeroBlockProps';
import IconSelect3dUltimate from '~/icon/ultimate/IconSelect3dUltimate';
import IconGalleryUltimate from '~/icon/ultimate/IconGalleryUltimate';
import { HeroLayout } from '~/editor/blocks/hero/lib';
import { css_gap, css_medium_up, css_quick, css_rail, css_rail_up, css_slow, css_small_down, css_text } from '~/emotion/theme';
import useMicrositePuck from '~/hooks/useMicrositePuck';

const Layout = styled.div`
	@media (${css_medium_up}) {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: ${css_gap('grid')};
	}
`;

const Nav = styled.nav`
	display: flex;
	align-items: center;
	gap: ${css_gap('content')};

	&:only-child {
		flex: 1;
		justify-content: space-evenly;
	}

	@media (${css_small_down}) {
		justify-content: space-between;
		padding: ${css_gap('content')} 0;
	}
`;

const NavLinkIcon = styled.span`
	width: 1.5rem;
	height: 1.5rem;
	flex-shrink: 0;
	transition: all ${css_slow};
`;

const NavLinkText = styled.span`
	transition: all ${css_slow};
`;

const NavLink = styled.a`
	text-transform: uppercase;
	justify-content: center;
	text-decoration: none;
	height: 3rem;
	align-items: center;
	transition: background-color ${css_quick};
	display: flex;
	font-weight: 300;
	gap: 0.5rem;
	padding: 0 ${css_gap('content')};
	color: ${css_text('strong', 'white')};
	background: ${css_text()};

	&:hover {
		background: ${css_text('weak')};

		${NavLinkIcon} {
			scale: 1.25;
		}

		${NavLinkText} {
			scale: 0.95;
		}
	}

	@media (${css_medium_up}) {
		background: color-mix(in srgb, ${css_text()}, transparent 10%);

		&:hover {
			background: color-mix(in srgb, ${css_text('weak')}, transparent 10%);
		}
	}

	@media (${css_small_down}) {
		container-type: inline-size;
		flex: 1;
	}
`;

const Price = styled.div`
	font-weight: 500;
	font-size: 1.8rem;
	justify-items: center;
	height: 3rem;
	display: flex;
	align-items: center;
	padding: 0 ${css_gap('content')};
	background: color-mix(in srgb, ${css_text()}, transparent 20%);
	color: ${css_text('strong', 'white')};

	@media (${css_small_down}) {
		display: none;
	}
	@media (${css_rail_up}) {
		justify-items: flex-start;
		width: ${css_rail};
	}
`;

const Optional = styled.span`
	@container (width < 9rem) {
		display: none;
	}
`;

export default function HeroBlockAlpha({ image }: HeroBlockData) {
	const listing = useListingContext();
	const data = useMicrositePuck();

	const tours = data.content.find(
		(component) =>
			component.type === 'Matterport' ||
			(component.type === 'Tab' &&
				component.props.slots.some((slot) => slot.content.some((slotComponent) => slotComponent.type === 'Matterport'))),
	);

	return (
		<HeroLayout image={image} height={50}>
			<Layout>
				<Nav>
					{tours && (
						<NavLink href={`#${tours.props.id}`}>
							<NavLinkIcon>
								<IconSelect3dUltimate />
							</NavLinkIcon>
							<NavLinkText>
								Virtual <Optional>Tour</Optional>
							</NavLinkText>
						</NavLink>
					)}
					<NavLink href="#gallery">
						<NavLinkIcon>
							<IconGalleryUltimate />
						</NavLinkIcon>
						<NavLinkText>
							<Optional>View</Optional> Gallery
						</NavLinkText>
					</NavLink>
				</Nav>
				{listing.price_formatted && <Price itemProp="price">${listing.price_formatted}</Price>}
			</Layout>
		</HeroLayout>
	);
}
